<template>
  <div>
    <v-card class="admission-map-card">
      <InfiniteScroll
        class="admission-map-table"
        fit-available-viewport
        mobile-breakpoint="0"
        enable-column-resize
        fit-resizable-columns-to-wrapper
        :resizable-columns="fieldsEnableToResize"
        :headers="headers"
        :items="records"
        :loading="loadingTable"
        :has-more="hasMore"
        :auto-load-on-mount="false"
        :item-class="setRowClass"
        :no-data-text="$t('admissionMap.messages.noDataText')"
        :loading-text="$t('admissionMap.messages.loadingText')"
        :no-result-text="$t('admissionMap.messages.noResultText')"
        @click.stop="(item, event) => onObserveRow(item, 'row')"
        @load-more="loadMoreRecords">
        <template v-slot:[`item.insuranceCarrierName`]="{ item }">
          <div @click.stop="onObserveRow(item)" class="admission-oneline-cell-table">
            <OneLineCell
              :ref="inputRefName(item.id, 'insuranceCarrierName')"
              :text="item.insuranceCarrierName"
            />
          </div>
        </template>
        <template v-slot:[`item.contractName`]="{ item }">
          <div @click.stop="onObserveRow(item)" class="admission-oneline-cell-table">
            <OneLineCell
              :ref="inputRefName(item.id, 'contractName')"
              :text="item.contractName"
            />
          </div>
        </template>
        <template v-slot:[`item.patientName`]="{ item }">
          <div @click.stop="onObserveRow(item)" class="admission-oneline-cell-table">
            <OneLineCell
              :ref="inputRefName(item.id, 'patientName')"
              :text="item.patientName"
            />
          </div>
        </template>
        <template v-slot:[`item.passwordStatus`]="{ item }">
          <div @click.stop="onObserveRow(item)" class="admission-oneline-cell-table">
            <OneLineCell
              :ref="inputRefName(item.id, 'passwordStatus')"
              :text="statusLabel(item.passwordStatus)"
            />
          </div>
        </template>
        <template v-slot:[`item.cidCode`]="{ item }">
          <Textfield
            :ref="inputRefName(item.id, 'cidCode')"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-field--disabled': isFieldReadOnly(item.id, 'cidCode'),
              'field-updated': isFieldUpdated(item.id, 'cidCode')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            v-bind="baseInputProps"
            :showTooltip="!!getFieldValue(item.id, 'cidCode')"
            :tooltip-value="getFieldValue(item.id, 'cidCode')"
            :solo="isFieldReadOnly(item.id, 'cidCode')"
            :flat="isFieldReadOnly(item.id, 'cidCode')"
            :readonly="isFieldReadOnly(item.id, 'cidCode')"
            :value="getFieldValue(item.id, 'cidCode')"
            @focus="onFieldFocus(item)"
            @click.stop="onObserveRow(item)"
            @input="onFieldChange(item.id, 'cidCode', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'cidCode')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'cidCode')"
          />
        </template>
        <template v-slot:[`item.cidDescription`]="{ item }">
          <Textfield
            :ref="inputRefName(item.id, 'cidDescription')"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'cidDescription')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            v-bind="baseInputProps"
            :showTooltip="!!getFieldValue(item.id, 'cidDescription')"
            :tooltip-value="getFieldValue(item.id, 'cidDescription')"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getFieldValue(item.id, 'cidDescription')"
            @focus="onFieldFocus(item)"
            @click.stop="onObserveRow(item)"
            @input="onFieldChange(item.id, 'cidDescription', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'cidDescription')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'cidDescription')"
          />
        </template>
        <template v-slot:[`item.speciality`]="{ item }">
          <Textfield
            :ref="inputRefName(item.id, 'speciality')"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'speciality')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            v-bind="baseInputProps"
            :showTooltip="!!getFieldValue(item.id, 'speciality')"
            :tooltip-value="getFieldValue(item.id, 'speciality')"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getFieldValue(item.id, 'speciality')"
            @focus="onFieldFocus(item)"
            @click.stop="onObserveRow(item)"
            @input="onFieldChange(item.id, 'speciality', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'speciality')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'speciality')"
          />
        </template>
        <template v-slot:[`item.urgencyType`]="{ item }">
          <Select
            :ref="inputRefName(item.id, 'urgencyType')"
            :append-icon="!isRowEditing(item.id) ? 'mdi-chevron-down' : ''"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-inline-field--boolean': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'urgencyType')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            :items="urgencyTypeOptions"
            :showTooltip="!!getFieldValue(item.id, 'urgencyType')"
            :tooltip-value="getFieldValue(item.id, 'urgencyType')"
            item-text="label"
            item-value="value"
            v-bind="baseInputProps"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :hideIcon="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'urgencyType')"
            @click.stop="onObserveRow(item, { focusField: 'urgencyType', openSelect: true })"
            @input="onFieldChange(item.id, 'urgencyType', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'urgencyType')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'urgencyType')"
          />
        </template>
        <template v-slot:[`item.highCost`]="{ item }">
          <Select
            :ref="inputRefName(item.id, 'highCost')"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-inline-field--boolean': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'highCost')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            :append-icon="!isRowEditing(item.id) ? 'mdi-chevron-down' : ''"
            :items="highCostOptions"
            item-text="label"
            item-value="value"
            v-bind="baseInputProps"
            :showTooltip="!!getFieldValue(item.id, 'highCost')"
            :tooltip-value="getFieldValue(item.id, 'highCost')"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :hideIcon="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'highCost')"
            @click.stop="onObserveRow(item, { focusField: 'highCost', openSelect: true })"
            @input="onFieldChange(item.id, 'highCost', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'highCost')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'highCost')"
          />
        </template>
        <template v-slot:[`item.unauthorized`]="{ item }">
          <Select
            :ref="inputRefName(item.id, 'unauthorized')"
            v-bind="baseInputProps"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-inline-field--boolean': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'unauthorized')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            :append-icon="!isRowEditing(item.id) ? 'mdi-chevron-down' : ''"
            :items="unauthorizedOptions"
            item-text="label"
            item-value="value"
            :showTooltip="!!getFieldValue(item.id, 'unauthorized')"
            :tooltip-value="getRawFieldValue(item.id, 'unauthorized')"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :hideIcon="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'unauthorized')"
            @click.stop="onObserveRow(item, { focusField: 'unauthorized', openSelect: true })"
            @input="onFieldChange(item.id, 'unauthorized', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'unauthorized')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'unauthorized')"
          />
        </template>
        <template v-slot:[`item.icu`]="{ item }">
          <Select
            :ref="inputRefName(item.id, 'icu')"
            :append-icon="!isRowEditing(item.id) ? 'mdi-chevron-down' : ''"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-inline-field--boolean': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'icu')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            :items="icuOptions"
            :showTooltip="!!getFieldValue(item.id, 'icu')"
            :tooltip-value="getRawFieldValue(item.id, 'icu')"
            item-text="label"
            item-value="value"
            v-bind="baseInputProps"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :hideIcon="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'icu')"
            @click.stop="onObserveRow(item, { focusField: 'icu', openSelect: true })"
            @input="onFieldChange(item.id, 'icu', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'icu')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'icu')"
          />
        </template>
        <template v-slot:[`item.medicalReport`]="{ item }">
          <Textfield
            :ref="inputRefName(item.id, 'medicalReport')"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'medicalReport')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            v-bind="baseInputProps"
            :showTooltip="!!getFieldValue(item.id, 'medicalReport')"
            :tooltip-value="getFieldValue(item.id, 'medicalReport')"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            readonly
            :value="getFieldValue(item.id, 'medicalReport')"
            @focus="onDialogFieldFocus(item, 'medicalReport')"
            @click.stop="onDialogFieldClick(item, 'medicalReport')"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'medicalReport')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'medicalReport')"
          />
        </template>
        <template v-slot:[`item.specialCase`]="{ item }">
          <Select
            :ref="inputRefName(item.id, 'specialCase')"
            :append-icon="!isRowEditing(item.id) ? 'mdi-chevron-down' : ''"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-inline-field--boolean': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'specialCase')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            :items="specialCaseOptions"
            :showTooltip="!!getFieldValue(item.id, 'specialCase')"
            :tooltip-value="getRawFieldValue(item.id, 'specialCase')"
            item-text="label"
            item-value="value"
            v-bind="baseInputProps"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            :hideIcon="!isRowEditing(item.id)"
            :readonly="!isRowEditing(item.id)"
            :value="getRawFieldValue(item.id, 'specialCase')"
            @click.stop="onObserveRow(item, { focusField: 'specialCase', openSelect: true })"
            @input="onFieldChange(item.id, 'specialCase', $event)"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'specialCase')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'specialCase')"
          />
        </template>
        <template v-slot:[`item.evolution`]="{ item }">
          <Textfield
            :ref="inputRefName(item.id, 'evolution')"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'evolution')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            v-bind="baseInputProps"
            :showTooltip="!!getFieldValue(item.id, 'evolution')"
            :tooltip-value="getFieldValue(item.id, 'evolution')"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            readonly
            :value="getFieldValue(item.id, 'evolution')"
            @focus="onDialogFieldFocus(item, 'evolution')"
            @click.stop="onDialogFieldClick(item, 'evolution')"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'evolution')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'evolution')"
          />
        </template>
        <template v-slot:[`item.note`]="{ item }">
          <Textfield
            :ref="inputRefName(item.id, 'note')"
            :class="{
              'admission-map-inline-field': true,
              'admission-map-field--disabled': !isRowEditing(item.id),
              'field-updated': isFieldUpdated(item.id, 'note')
            }"
            :textColor="rowError(item) ? 'foreground-accent' : ''"
            v-bind="baseInputProps"
            :showTooltip="!!getFieldValue(item.id, 'note')"
            :tooltip-value="getFieldValue(item.id, 'note')"
            :solo="!isRowEditing(item.id)"
            :flat="!isRowEditing(item.id)"
            readonly
            :value="getFieldValue(item.id, 'note')"
            @focus="onDialogFieldFocus(item, 'note')"
            @click.stop="onDialogFieldClick(item, 'note')"
            @keydown.tab.exact.prevent="focusNextField(item.id, 'note')"
            @keydown.shift.tab.prevent="focusPreviousField(item.id, 'note')"
          />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex flex-row justify-center align-center">
            <v-icon
              v-if="rowSuccess(item)"
              color="primary"
              size="18">
              fas fa-check
            </v-icon>

            <v-tooltip
              bottom
              v-if="rowError(item)">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :loading="loadingUpdate && rowIdUpdated === item.id"
                  v-on="on"
                  v-bind="attrs"
                  @click="trySaveAgain(item)"
                  solo
                  icon>
                  <v-img
                    contain
                    height="18"
                    src="icons/map-save-white.svg"
                  />
                </v-btn>
              </template>
              <span>
                {{ $t('common.save') }}
              </span>
            </v-tooltip>

            <v-menu
              v-if="rowError(item)"
              bottom
              offset-y
              open-on-hover
              :close-delay="150"
              :open-delay="100"
              content-class="admission-map-row-error-menu">
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="d-inline-flex"
                  v-on="on"
                  v-bind="attrs">
                  <v-img
                    contain
                    height="18"
                    src="icons/alert-triangle.svg"
                  />
                </div>
              </template>

              <v-card class="admission-map-row-error-card" outlined>
                <div
                  v-if="hasRowErrorItems(item.id)"
                  class="admission-map-row-error-card__body">
                  <ul class="admission-map-row-error-card__list">
                    <li
                      v-for="(message, index) in getRowErrorItems(item.id)"
                      :key="`${item.id}-error-${index}`">
                      {{ message }}
                    </li>
                  </ul>
                </div>

                <div
                  v-else
                  class="admission-map-row-error-card__body">
                  {{ getRowErrorMessage(item.id) }}
                </div>
              </v-card>
            </v-menu>

            <v-tooltip
              bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  :loading="loadingRecordHistories && recordIdHistoryInLoading === item.id"
                  :disabled="loadingRecordHistories && recordIdHistoryInLoading !== item.id"
                  @click="showRecordHistory(item.id)"
                  solo
                  icon>
                  <v-icon
                    size="26"
                    color="base-color">
                    mdi mdi-history
                  </v-icon>
                </v-btn>
              </template>
              <span>
                {{ $t('admissionMap.actions.viewHistory') }}
              </span>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="py-6 text-center admission-map-text-base grey--text">
            {{ $t('admissionMap.table.noData') }}
          </div>
        </template>
      </InfiniteScroll>
    </v-card>

    <AdmissionMapFieldDialog
      :key="`${fieldDialog.rowId || 'no-row'}-${fieldDialog.field || 'no-field'}`"
      :visible="fieldDialog.visible"
      :value="fieldDialog.value"
      :title="fieldDialogTitle"
      @cancel="cancelFieldDialog"
      @save="saveFieldDialog"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import {
  formatBooleanValue,
  formatOptionLabel,
  formatPasswordStatus,
} from '@/store/modules/admission-map/admission-map-config-types';
import InfiniteScroll from '@/components/Default/InfiniteScroll.vue';
import OneLineCell from '@/components/Default/OneLineCell.vue';
import AdmissionMapFieldDialog from '@/components/AdmissionMap/AdmissionMapFieldDialog.vue';
import Formatters from '@/shared/formatters/formatters';
import AuthorityManagementMixin from '@/shared/mixins/authorityManagement/authorityManagementMixin';
import Select from '../Default/Select.vue';
import Textfield from '../Default/Textfield.vue';

export default {
  name: 'AdmissionMapTable',
  mixins: [AuthorityManagementMixin],
  components: {
    InfiniteScroll,
    OneLineCell,
    AdmissionMapFieldDialog,
    Textfield,
    Select,
  },
  data: () => ({
    formatter: null,
    formRows: {},
    updatedFields: {},
    editingRows: {},
    fieldsEnableToResize: [
      'insuranceCarrierName',
      'contractName',
      'patientName',
      'passwordStatus',
      'cidCode',
      'cidDescription',
      'speciality',
      'urgencyType',
      'highCost',
      'unauthorized',
      'icu',
      'medicalReport',
      'specialCase',
      'evolution',
      'note'
    ],
    editableFields: [
      'cidCode',
      'cidDescription',
      'speciality',
      'urgencyType',
      'highCost',
      'unauthorized',
      'icu',
      'medicalReport',
      'specialCase',
      'evolution',
      'note'
    ],
    dialogEditableFields: [
      'medicalReport',
      'evolution',
      'note',
    ],
    fieldDialog: {
      visible: false,
      rowId: null,
      field: null,
      value: '',
    },
    currentlyFocusedField: null,
    rowErrors: {},
    recordIdHistoryInLoading: null,
    rowIdUpdated: null,
    rowIdWithError: [],
    skipDialogFieldFocus: {},
  }),
  computed: {
    ...mapGetters({
      loadingRecordHistories: 'admission-map/loadingRecordHistories',
      unauthorizedOptions: 'admission-map/unauthorizedOptions',
      specialCaseOptions: 'admission-map/specialCaseOptions',
      highCostOptions: 'admission-map/highCostOptions',
      urgencyTypeOptions: 'admission-map/urgencyTypeOptions',
      loadingUpdate: 'admission-map/loadingUpdate',
      loadingTable: 'admission-map/loadingTable',
      itemsPerPage: 'admission-map/itemsPerPage',
      currentPage: 'admission-map/currentPage',
      icuOptions: 'admission-map/icuOptions',
      totalItems: 'admission-map/totalItems',
      clickAway: 'admission-map/clickAway',
      sortDesc: 'admission-map/sortDesc',
      records: 'admission-map/records',
      sortBy: 'admission-map/sortBy',
    }),
    headers() {
      return [
        { text: this.$t('admissionMap.fields.carrier'), value: 'insuranceCarrierName', sortable: true, width: 95, class: 'col-carrier', cellClass: 'col-carrier' },
        { text: this.$t('admissionMap.fields.contract'), value: 'contractName', sortable: true, width: 95, class: 'col-contract', cellClass: 'col-contract' },
        { text: this.$t('admissionMap.fields.patient'), value: 'patientName', sortable: true, width: 95, class: 'col-patient', cellClass: 'col-patient' },
        { text: this.$t('admissionMap.fields.passwordStatus'), value: 'passwordStatus', sortable: true, width: 80, class: 'col-status', cellClass: 'col-status' },
        { text: this.$t('admissionMap.fields.cidCode'), value: 'cidCode', sortable: false, width: 125, class: 'col-cid', cellClass: 'col-cid' },
        { text: this.$t('admissionMap.fields.cidDescription'), value: 'cidDescription', sortable: true, width: 90, class: 'col-cid-description', cellClass: 'col-cid-description' },
        { text: this.$t('admissionMap.fields.speciality'), value: 'speciality', sortable: true, width: 120, class: 'col-speciality', cellClass: 'col-speciality' },
        { text: this.$t('admissionMap.fields.urgencyType'), value: 'urgencyType', sortable: true, width: 100, class: 'col-urgency-type', cellClass: 'col-urgency-type' },
        { text: this.$t('admissionMap.fields.highCost'), value: 'highCost', sortable: true, width: 95, class: 'col-highcost', cellClass: 'col-highcost' },
        { text: this.$t('admissionMap.fields.unauthorized'), value: 'unauthorized', sortable: true, width: 150, class: 'col-unauthorized', cellClass: 'col-unauthorized' },
        { text: this.$t('admissionMap.fields.icu'), value: 'icu', sortable: true, width: 95, class: 'col-icu', cellClass: 'col-icu' },
        { text: this.$t('admissionMap.fields.medicalReport'), value: 'medicalReport', sortable: true, width: 85, class: 'col-report', cellClass: 'col-report' },
        { text: this.$t('admissionMap.fields.specialCase'), value: 'specialCase', sortable: true, width: 95, class: 'col-special-case', cellClass: 'col-special-case' },
        { text: this.$t('admissionMap.fields.evolution'), value: 'evolution', sortable: true, width: 90, class: 'col-evolution', cellClass: 'col-evolution' },
        { text: this.$t('admissionMap.table.headers.note'), value: 'note', sortable: true, width: 160, class: 'col-note', cellClass: 'col-note' },
        { text: this.$t('common.actions'), value: 'actions', sortable: false, width: 65, align: 'center', class: 'col-action text-center', cellClass: 'col-action text-center' },
      ];
    },
    baseInputProps() {
      return {
        dense: true,
        hideDetails: true,
      };
    },
    hasMore() {
      if (!this.totalItems) return false;
      return this.records.length < this.totalItems;
    },
    fieldDialogTitle() {
      return this.getFieldDialogTitle(this.fieldDialog.field);
    },
  },
  watch: {
    records: {
      handler(records) {
        this.syncFormRows(records);
      },
      immediate: true,
    },
    clickAway: {
      handler(clicked) {
        if (clicked) {
          this.onObserveRow(null);
        }
      }
    }
  },
  created() {
    this.formatter = new Formatters();
  },
  methods: {
    ...mapActions({
      fetchAdmissionHistory: 'admission-map/fetchAdmissionHistory',
      updateTableOptions: 'admission-map/updateTableOptions',
      selectRecord: 'admission-map/selectRecord',
      updateRecord: 'admission-map/updateRecord',
    }),
    rowSuccess(item) {
      const isSuccess = this.setRowClass(item) && this.setRowClass(item).includes('success');
      return !!isSuccess;
    },
    rowError(item) {
      const isError = this.setRowClass(item) && this.setRowClass(item).includes('error');
      return !!isError;
    },
    getRowError(rowId) {
      return this.rowErrors[rowId] || null;
    },
    getRowErrorMessage(rowId) {
      const rowError = this.getRowError(rowId);
      return rowError && rowError.message
        ? rowError.message
        : this.$t('admissionMap.messages.saveError');
    },
    getRowErrorItems(rowId) {
      const rowError = this.getRowError(rowId);
      return rowError && Array.isArray(rowError.items) ? rowError.items : [];
    },
    hasRowErrorItems(rowId) {
      return this.getRowErrorItems(rowId).length > 0;
    },
    setRowErrorInfo(rowId, errorInfo) {
      this.$set(this.rowErrors, rowId, errorInfo);
    },
    clearRowErrorInfo(rowId) {
      if (this.rowErrors[rowId]) {
        this.$delete(this.rowErrors, rowId);
      }
    },
    getUpdateErrorResponse(updateResult) {
      if (!updateResult || typeof updateResult !== 'object') return null;
      return updateResult.response || null;
    },
    isValidationEntityError(errorData) {
      return Boolean(
        errorData &&
        Number(errorData.code) === 422 &&
        Array.isArray(errorData.fields)
      );
    },
    translateValidationFieldMessage(field, fallbackMessage) {
      const translationKey = `admissionMap.messages.validationFields.${field}`;
      const translated = String(this.$t(translationKey));

      if (translated && translated !== translationKey) {
        return translated;
      }

      return fallbackMessage || String(this.$t('admissionMap.messages.saveError'));
    },
    buildValidationFieldErrorItems(errorData) {
      if (!this.isValidationEntityError(errorData)) return [];

      const tableFieldOrder = new Map(
        this.headers.map((header, index) => [header.value, index])
      );
      tableFieldOrder.set('uti', tableFieldOrder.get('icu'));
      const sortedFields = errorData.fields
        .map((item, originalIndex) => ({ item, originalIndex }))
        .filter(({ item }) => Boolean(item))
        .sort((a, b) => {
          const aOrder = tableFieldOrder.has(a.item.field)
            ? tableFieldOrder.get(a.item.field)
            : Number.MAX_SAFE_INTEGER;
          const bOrder = tableFieldOrder.has(b.item.field)
            ? tableFieldOrder.get(b.item.field)
            : Number.MAX_SAFE_INTEGER;

          if (aOrder !== bOrder) return aOrder - bOrder;
          return a.originalIndex - b.originalIndex;
        });

      const uniqueMessages = new Set();
      sortedFields.forEach(({ item }) => {
        const message = this.translateValidationFieldMessage(item.field, item.message);
        if (message) uniqueMessages.add(message);
      });

      return Array.from(uniqueMessages);
    },
    buildRowErrorInfo(status, errorData) {
      if (status === 403) {
        return {
          message: String(this.$t('admissionMap.messages.noPermission')),
          items: [],
        };
      }

      if (this.isValidationEntityError(errorData)) {
        const items = this.buildValidationFieldErrorItems(errorData);

        if (items.length > 0) {
          return {
            message: String(this.$t('admissionMap.messages.saveError')),
            items,
          };
        }
      }

      return {
        message: String(this.$t('admissionMap.messages.saveError')),
        items: [],
      };
    },
    async trySaveAgain(item) {
      await this.saveRecord(item.id);
    },
    async showRecordHistory(rowId) {
      this.recordIdHistoryInLoading = rowId;
      await this.fetchAdmissionHistory(rowId);
      this.recordIdHistoryInLoading = null;
    },
    clearUpdatedFields(rowId) {
      if (this.updatedFields[rowId]) {
        this.$set(this.updatedFields, rowId, {});
      }
    },
    disableEditing(rowId) {
      if (this.editingRows[rowId]) {
        this.$set(this.editingRows, rowId, false);
      }
      if (this.fieldDialog.rowId === rowId) {
        this.closeFieldDialog(false);
      }
    },
    enableEditing(rowId, focusField = 'cidDescription') {
      if (!this.canEditRow(rowId)) return false;
      this.ensureFormRow(rowId);
      this.$set(this.editingRows, rowId, true);
      if (focusField) {
        this.$nextTick(() => {
          this.focusField(rowId, focusField);
        });
      }
      return true;
    },
    canEditRow() {
      return this.hasPermission('sdi_mapa_internacao_editar');
    },
    ensureFormRow(rowId) {
      if (!this.formRows[rowId]) {
        const record = this.findRecord(rowId);
        this.$set(this.formRows, rowId, record ? { ...record } : { id: rowId });
      }
    },
    findRecord(rowId) {
      return this.records.find((record) => record.id === rowId);
    },
    async finalizeRowEdit(rowId) {
      if (!rowId) return true;
      if (this.rowHasChanges(rowId)) {
        const success = await this.saveRecord(rowId);
        if (!success) {
          this.currentlyFocusedField = rowId;
          return false;
        }
      } else {
        this.clearUpdatedFields(rowId);
        this.disableEditing(rowId);
      }
      if (this.currentlyFocusedField === rowId) {
        this.currentlyFocusedField = null;
      }
      return true;
    },
    async focusPreviousField(rowId, field) {
      if (!this.isRowEditing(rowId)) return;
      const fields = this.editableFields;
      const fieldIndex = fields.indexOf(field);
      const rowIndex = this.records.findIndex((record) => record.id === rowId);
      if (fieldIndex === -1 || rowIndex === -1) return;

      let targetRowIndex = rowIndex;
      let targetFieldIndex = fieldIndex - 1;

      if (targetFieldIndex < 0) {
        targetRowIndex = rowIndex - 1;
        targetFieldIndex = fields.length - 1;
      }

      if (targetRowIndex < 0) return;

      const targetRowId = this.records[targetRowIndex]?.id;
      const targetField = fields[targetFieldIndex];

      if (!targetRowId || !targetField) return;

      if (targetRowIndex !== rowIndex) {
        const finalized = await this.finalizeRowEdit(rowId);
        if (!finalized) return;
        const rowEditingEnabled = this.enableEditing(targetRowId, targetField);
        if (rowEditingEnabled) {
          this.currentlyFocusedField = targetRowId;
        }
        return;
      }

      this.$nextTick(() => {
        this.focusField(targetRowId, targetField);
      });
    },
    async focusNextField(rowId, field) {
      if (!this.isRowEditing(rowId)) return;
      const fields = this.editableFields;
      const fieldIndex = fields.indexOf(field);
      const rowIndex = this.records.findIndex((record) => record.id === rowId);
      if (fieldIndex === -1 || rowIndex === -1) return;

      let targetRowIndex = rowIndex;
      let targetFieldIndex = fieldIndex + 1;

      if (targetFieldIndex >= fields.length) {
        targetRowIndex = rowIndex + 1;
        targetFieldIndex = 0;
      }

      if (targetRowIndex >= this.records.length) return;

      const targetRowId = this.records[targetRowIndex]?.id;
      const targetField = fields[targetFieldIndex];

      if (!targetRowId || !targetField) return;

      if (targetRowIndex !== rowIndex) {
        const finalized = await this.finalizeRowEdit(rowId);
        if (!finalized) return;
        const rowEditingEnabled = this.enableEditing(targetRowId, targetField);
        if (rowEditingEnabled) {
          this.currentlyFocusedField = targetRowId;
        }
        return;
      }

      this.$nextTick(() => {
        this.focusField(targetRowId, targetField);
      });
    },
    focusField(rowId, field) {
      const refName = this.inputRefName(rowId, field);
      const ref = this.$refs[refName];
      const component = Array.isArray(ref) ? ref[0] : ref;
      if (component && typeof component.focus === 'function') {
        component.focus();
      }
    },
    openSelectMenu(rowId, field) {
      const refName = this.inputRefName(rowId, field);
      const ref = this.$refs[refName];
      const component = Array.isArray(ref) ? ref[0] : ref;
      if (component && typeof component.openMenu === 'function') {
        component.openMenu();
      }
    },
    isDialogEditableField(field) {
      return this.dialogEditableFields.includes(field);
    },
    getFieldDialogTitle(field) {
      switch (field) {
        case 'note':
          return this.$t('admissionMap.dialog.editObservationTitle');
        case 'medicalReport':
          return this.$t('admissionMap.fields.medicalReport');
        case 'evolution':
          return this.$t('admissionMap.fields.evolution');
        default:
          return '';
      }
    },
    isFieldValueEmpty(value) {
      return this.formatter.isEmptyValue(value)
        || (typeof value === 'string' && value.trim().length === 0);
    },
    consumeSkipDialogFieldFocus(field) {
      if (!field || !this.skipDialogFieldFocus[field]) return false;
      this.$set(this.skipDialogFieldFocus, field, false);
      return true;
    },
    setSkipDialogFieldFocus(field, shouldSkip) {
      if (!field) return;
      this.$set(this.skipDialogFieldFocus, field, shouldSkip);
    },
    hasRowUpdates(rowId) {
      const updated = this.updatedFields[rowId];
      return Boolean(updated && Object.keys(updated).length > 0);
    },
    rowHasChanges(rowId) {
      if (this.hasRowUpdates(rowId)) return true;
      const record = this.findRecord(rowId);
      const formRow = this.formRows[rowId];
      if (!record || !formRow) return false;
      return this.editableFields.some((field) => (
        this.normalizeComparisonValue(formRow[field]) !== this.normalizeComparisonValue(record[field])
      ));
    },
    async onDialogFieldClick(item, field) {
      if (!this.isDialogEditableField(field)) return;
      await this.onObserveRow(item);
      this.openFieldDialog(item, field);
    },
    async onObserveRow(item, options = {}) {
      const { focusField = 'cidDescription', openSelect = false } = options;
      if (!item || !item.id) {
        if (this.currentlyFocusedField) {
          const finalized = await this.finalizeRowEdit(this.currentlyFocusedField);
          if (!finalized) return;
        }
        return;
      }

      if (this.currentlyFocusedField != null && item.id != this.currentlyFocusedField) {
        const finalized = await this.finalizeRowEdit(this.currentlyFocusedField);
        if (!finalized) return;
      }

      if (!this.isRowEditing(item.id)) {
        const rowEditingEnabled = this.enableEditing(item.id, focusField);
        if (!rowEditingEnabled) return;

        this.currentlyFocusedField = item.id;
        if (openSelect) {
          this.$nextTick(() => {
            this.openSelectMenu(item.id, focusField);
          });
        }
        return;
      }
    },
    onFieldFocus(item) {
      this.onObserveRow(item);
    },
     async onDialogFieldFocus(item, field) {
      if (!this.isDialogEditableField(field)) {
        await this.onObserveRow(item);
        return;
      }

      if (this.consumeSkipDialogFieldFocus(field)) {
        return;
      }

      await this.onObserveRow(item);

      const fieldValue = this.getFieldValue(item.id, field);
      if (this.isFieldValueEmpty(fieldValue)) {
        this.openFieldDialog(item, field);
      }
    },
    openFieldDialog(item, field) {
      if (!item || !item.id || !field || !this.isDialogEditableField(field)) return;
      if (!this.isRowEditing(item.id)) return;
      if (
        this.fieldDialog.visible &&
        this.fieldDialog.rowId === item.id &&
        this.fieldDialog.field === field
      ) {
        return;
      }

      this.fieldDialog.rowId = item.id;
      this.fieldDialog.field = field;
      this.fieldDialog.value = this.getFieldValue(item.id, field);
      this.fieldDialog.visible = true;
    },
    closeFieldDialog(restoreFocus = true) {
      const { rowId, field } = this.fieldDialog;

      this.fieldDialog.visible = false;
      this.fieldDialog.rowId = null;
      this.fieldDialog.field = null;
      this.fieldDialog.value = '';

      if (restoreFocus && rowId && field) {
        this.setSkipDialogFieldFocus(field, true);
        this.$nextTick(() => {
          this.focusField(rowId, field);
        });
        return;
      }

      if (field) {
        this.setSkipDialogFieldFocus(field, false);
      }
    },
    cancelFieldDialog() {
      this.closeFieldDialog(true);
    },
    applyDialogFieldSave(rowId, field, value) {
      switch (field) {
        case 'medicalReport':
          this.onFieldChange(rowId, 'medicalReport', value);
          break;
        case 'evolution':
          this.onFieldChange(rowId, 'evolution', value);
          break;
        case 'note':
          this.onFieldChange(rowId, 'note', value);
          break;
        default:
          break;
      }
    },
    saveFieldDialog(value) {
      const { rowId, field } = this.fieldDialog;
      if (rowId && field) {
        this.applyDialogFieldSave(rowId, field, value);
      }
      this.closeFieldDialog(true);
    },
    getFieldValue(rowId, field) {
      const formRow = this.formRows[rowId];
      if (formRow && Object.prototype.hasOwnProperty.call(formRow, field)) {
        return this.formatFieldDisplayValue(field, formRow[field]);
      }
      const record = this.findRecord(rowId);
      return this.formatFieldDisplayValue(field, record ? record[field] : '');
    },
    getRawFieldValue(rowId, field) {
      if (this.formRows[rowId] && Object.prototype.hasOwnProperty.call(this.formRows[rowId], field)) {
        return this.formRows[rowId][field];
      }
      const record = this.findRecord(rowId);
      return record ? record[field] : null;
    },
    isCidNotSpecified(value) {
      if (this.formatter.isEmptyValue(value)) return false;
      return String(value).trim() === '-1';
    },
    formatFieldDisplayValue(field, value) {
      if (field === 'cidCode' && this.isCidNotSpecified(value)) {
        return String(this.$t('admissionMap.messages.notSpecified'));
      }
      if (field === 'highCost') {
        if (this.formatter.isEmptyValue(value)) return '';
        return formatOptionLabel(value, this.highCostOptions);
      }
      if (field === 'urgencyType') {
        if (this.formatter.isEmptyValue(value)) return '';
        return formatOptionLabel(value, this.urgencyTypeOptions);
      }
      return this.normalizeDisplayValue(value);
    },
    isCidEditable(rowId) {
      const row = this.formRows[rowId] || this.findRecord(rowId);

      if (!row || typeof row.isCidEditable !== 'boolean') {
        return false;
      }

      return row.isCidEditable;
    },
    isFieldReadOnly(rowId, field) {
      if (!this.isRowEditing(rowId)) return true;
      if (field === 'cidCode' && !this.isCidEditable(rowId)) return true;
      return false;
    },
    inputRefName(rowId, field) {
      return `admission-field-${rowId}-${field}`;
    },
    isFieldUpdated(rowId, field) {
      return Boolean(this.updatedFields[rowId] && this.updatedFields[rowId][field]);
    },
    isRowEditing(rowId) {
      return Boolean(this.editingRows[rowId]);
    },
    markFieldAsUpdated(rowId, field, isUpdate) {
      if (!this.updatedFields[rowId]) {
        this.$set(this.updatedFields, rowId, {});
      }
      if (isUpdate) {
        this.$set(this.updatedFields[rowId], field, true);
      } else if (this.updatedFields[rowId][field]) {
        this.$delete(this.updatedFields[rowId], field);
      }
    },
    normalizeComparisonValue(value) {
      if (this.formatter.isEmptyValue(value)) return '';
      if (typeof value === 'boolean') return formatBooleanValue(value);
      return String(value);
    },
    normalizeDisplayValue(value) {
      if (this.formatter.isEmptyValue(value)) return '';
      if (typeof value === 'boolean') return formatBooleanValue(value);
      return value;
    },
    onFieldChange(rowId, field, value) {
      if (!this.isRowEditing(rowId)) return;
      if (field === 'cidCode' && !this.isCidEditable(rowId)) return;
      this.ensureFormRow(rowId);
      this.$set(this.formRows[rowId], field, value);
      this.updateFieldState(rowId, field, value);
      this.savePartialRecord(rowId);
    },
    statusLabel(status) {
      return formatPasswordStatus(status);
    },
    syncFormRows(records) {
      const nextFormRows = {};
      const nextupdatedFields = {};
      const nextEditingRows = {};
      records.forEach((record) => {
        const existingRow = this.formRows[record.id] || {};
        const updated = this.updatedFields[record.id] || {};
        const hasUpdates = Object.keys(updated).length > 0;
        nextFormRows[record.id] = hasUpdates ? { ...record, ...existingRow } : { ...record };
        nextupdatedFields[record.id] = updated;
        nextEditingRows[record.id] = Boolean(this.editingRows[record.id]);
      });
      this.formRows = nextFormRows;
      this.updatedFields = nextupdatedFields;
      this.editingRows = nextEditingRows;
    },
    mountRecord(rowId) {
      return {
        ...(this.findRecord(rowId) || {}),
        ...(this.formRows[rowId] || {}),
      };
    },
    savePartialRecord(rowId) {
      const payload = this.mountRecord(rowId);
      this.selectRecord(payload);
    },
    async sendRecord(rowId, topValue) {
      const payload = this.mountRecord(rowId);
      const updateResult = await this.updateRecord(payload, topValue);
      const errorResponse = this.getUpdateErrorResponse(updateResult);

      if (errorResponse) {
        this.addRowIdWithError(rowId);
        this.setRowErrorInfo(rowId, this.buildRowErrorInfo(errorResponse.status, errorResponse.data));
        return false;
      }

      switch(updateResult) {
        case 200:
          this.rowIdUpdated = rowId;
          this.removeRowIdWithError(rowId);
          setTimeout(() => {
            this.rowIdUpdated = null;
          }, 5000);
          return true;
        default:
          this.addRowIdWithError(rowId);
          this.setRowErrorInfo(rowId, this.buildRowErrorInfo(updateResult, null));
          return false;
      }
    },
    addRowIdWithError(rowId) {
      if (!this.rowIdWithError.includes(rowId)) {
        this.rowIdWithError = [...this.rowIdWithError, rowId];
      }
    },
    removeRowIdWithError(rowId) {
      if (this.rowIdWithError.includes(rowId)) {
        this.rowIdWithError = this.rowIdWithError.filter((id) => id !== rowId);
      }
      this.clearRowErrorInfo(rowId);
    },
    setRowClass(item) {
      if (this.rowIdUpdated === item.id) {
        return 'success';
      }

      if (this.rowIdWithError.includes(item.id)) {
        return 'error foreground-accent--text';
      }
    },
    async saveRecord(rowId) {
      const refLine = this.inputRefName(rowId, 'cidDescription');
      const refInput = this.$refs[refLine];
      const elInput = refInput && refInput.$el ? refInput.$el : null;
      const topValue = elInput ? elInput.getBoundingClientRect().top-4 : 50;

      const success = await this.sendRecord(rowId, topValue);
      if (success) {
        this.clearUpdatedFields(rowId);
        this.disableEditing(rowId);
      }
      return success;
    },
    loadMoreRecords() {
      if (!this.hasMore || this.loadingTable) return;
      this.updateTableOptions({
        page: this.currentPage + 1,
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortBy ? [this.sortBy] : [],
        sortDesc: this.sortBy ? [this.sortDesc] : [],
      });
    },
    updateFieldState(rowId, field, value) {
      const record = this.findRecord(rowId);
      const original = record ? record[field] : '';
      const isUpdate = this.normalizeComparisonValue(value) !== this.normalizeComparisonValue(original);
      this.markFieldAsUpdated(rowId, field, isUpdate);
    },
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/admissionMap/table.scss";
</style>
